import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
} from '@brand/components';
import {
  IFieldProps,
  ISelectFieldSchema,
  ISelectFieldStyles,
} from 'schema/generator/FormSchema';

import { useErrorMessage } from 'hooks/form/useErrorMessage';
import { useStyles } from 'hooks/form/useStyles';

import ComponentIsVisible from 'components/utils/IsVisible';

export interface ISelectFieldOptionsType<T = number, S = unknown> {
  additional?: S;
  label: string;
  value: T;
}

export const SelectField = ({
  defaultValue,
  field,
  id,
  isDisabled,
  name,
}: IFieldProps<ISelectFieldSchema>): JSX.Element => {
  const { label, isRequired, shouldDisplay, styles = {}, handleChange } = field;

  const { t } = useTranslation('form');
  const { register, watch } = useFormContext();
  const fieldStyles = useStyles<ISelectFieldStyles>('selectField', styles);
  const errorMessage = useErrorMessage(name, label);

  const values = watch(name);

  const isVisible = useMemo(() => {
    return shouldDisplay ? shouldDisplay(values) : true;
  }, [values, shouldDisplay]);

  return (
    <ComponentIsVisible when={isVisible}>
      <FormControl
        isInvalid={!!errorMessage}
        isRequired={isRequired}
        key={`${name}-control`}
        onChange={handleChange}
        {...fieldStyles.control}
      >
        <ComponentIsVisible when={!!label}>
          <FormLabel htmlFor={name} variant="proRikApp" {...fieldStyles.label}>
            {label}
          </FormLabel>
        </ComponentIsVisible>
        <Select
          data-testid={id}
          defaultValue={defaultValue || field.defaultValue}
          variant="proRikApp"
          {...register(name)}
          {...fieldStyles.select}
          disabled={isDisabled || false}
        >
          {field.options.map((option, index) => (
            <option
              key={option.value}
              style={
                index % 2 === 0
                  ? {
                      fontSize: '16px',
                      backgroundColor: `#F4F8FB`,
                    }
                  : {
                      fontSize: '16px',
                    }
              }
              value={option.value}
            >
              {option.label || option.value}
            </option>
          ))}
        </Select>
        <ComponentIsVisible when={!!errorMessage}>
          <FormErrorMessage {...fieldStyles.errorMessage}>
            {t(`validations.${errorMessage as string}`)}
          </FormErrorMessage>
        </ComponentIsVisible>
      </FormControl>
    </ComponentIsVisible>
  );
};
